import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import TeamHelmet from '../components/TeamHelmet'
import {
  PLAYOFF_PAGE_ROUNDS,
  PLAYOFF_ROUND_1_SERIES,
  PLAYOFF_ROUND_2_SERIES,
  PLAYOFF_ROUND_3_SERIES,
} from '../constants/playoffs'
import type { PlayoffSeriesPairing } from '../constants/playoffs'
import { getLeagueTeamDisplayName } from '../constants/teamNames'
import {
  buildMatchupResultsMap,
  buildPlayoffSeriesScheduleGames,
  buildUserByTeamAndRound,
  getUserMatchupKey,
  resolveVisibleMatchupRecordForPlayoffRound,
  type PlayoffCompetitorInput,
  type PlayoffMatchupResult,
  type PlayoffScheduleSlot,
} from '../lib/playoffSeriesStats'
import api from '../services/api'
import './GroupPlay.css'
import './PlayoffGames.css'

interface PlayoffGameSummary {
  id: number
  homeTeam: string
  awayTeam: string
  homeScore: number
  awayScore: number
  uploadedAt: string
  player1User?: {
    id: number
    displayName: string
    teamAbbreviation: string
  }
  player2User?: {
    id: number
    displayName: string
    teamAbbreviation: string
  }
  forfeit?: {
    outcome: 'home_win' | 'away_win' | 'double_loss'
    resultByUserId: Record<number, 'win' | 'loss'>
  }
}

function buildSeriesSchedules(
  pairings: PlayoffSeriesPairing[],
  userByTeamAndRound: Map<string, { id: number; displayName: string; groupPlaySlot: string | null }>,
  users: PlayoffCompetitorInput[]
) {
  return pairings.map(pairing => ({
    ...pairing,
    games: buildPlayoffSeriesScheduleGames(pairing, userByTeamAndRound, users),
  }))
}

/**
 * Shows the playoff game schedule with one same-round matchup per player, by bracket round.
 */
export default function Playoffs() {
  const navigate = useNavigate()
  const [users, setUsers] = useState<PlayoffCompetitorInput[]>([])
  const [games, setGames] = useState<PlayoffGameSummary[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadData = async () => {
      try {
        const [usersResponse, gamesResponse] = await Promise.all([
          api.get<PlayoffCompetitorInput[]>('/users'),
          api.get<PlayoffGameSummary[]>('/games'),
        ])
        setUsers(usersResponse.data ?? [])
        setGames(gamesResponse.data ?? [])
      } catch (error) {
        console.error('Failed to load playoff schedule:', error)
      } finally {
        setLoading(false)
      }
    }

    void loadData()
  }, [])

  const userByTeamAndRound = useMemo(() => buildUserByTeamAndRound(users), [users])
  const matchupResults = useMemo(() => buildMatchupResultsMap(games), [games])

  const round1Series = useMemo(
    () => buildSeriesSchedules(PLAYOFF_ROUND_1_SERIES, userByTeamAndRound, users),
    [userByTeamAndRound, users]
  )

  const round2Series = useMemo(
    () => buildSeriesSchedules(PLAYOFF_ROUND_2_SERIES, userByTeamAndRound, users),
    [userByTeamAndRound, users]
  )

  const round3Series = useMemo(
    () => buildSeriesSchedules(PLAYOFF_ROUND_3_SERIES, userByTeamAndRound, users),
    [userByTeamAndRound, users]
  )

  const seriesByPlayoffRound = useMemo(
    () =>
      new Map<1 | 2 | 3, ReturnType<typeof buildSeriesSchedules>>([
        [1, round1Series],
        [2, round2Series],
        [3, round3Series],
      ]),
    [round1Series, round2Series, round3Series]
  )

  return (
    <div className="group-play-page playoff-games-page">
      <h1>Playoffs</h1>
      <p className="group-play-subtitle">
        Each playoff round uses one same-draft-round game per player. Rematches between the same two
        players appear as separate rows when scheduled.
      </p>

      {loading && <p className="group-play-subtitle">Loading playoff assignments...</p>}

      {PLAYOFF_PAGE_ROUNDS.map(section => (
        <section key={section.playoffRound} className="playoff-round-section">
          <h2 className="playoff-round-heading">{section.heading}</h2>
          <div className="group-play-grid playoff-games-grid">
            {(seriesByPlayoffRound.get(section.playoffRound) ?? []).map(series => (
              <article key={series.id} className="group-play-card playoff-series-card">
                <h3>
                  {getLeagueTeamDisplayName(series.teamA)} vs {getLeagueTeamDisplayName(series.teamB)}
                </h3>
                <p className="playoff-series-subtitle">
                  {series.teamA} vs {series.teamB}
                </p>
                <div className="group-play-table-wrapper">
                  <table className="group-play-table playoff-matchup-table">
                    <thead>
                      <tr>
                        <th className="playoff-matchup-table__home">Home</th>
                        <th className="group-play-vs-column playoff-matchup-table__round">Round</th>
                        <th className="playoff-matchup-table__away">Away</th>
                      </tr>
                    </thead>
                    <tbody>
                      {series.games.map(scheduleGame => (
                        <PlayoffGameRow
                          key={scheduleGame.scheduleKey}
                          scheduleGame={scheduleGame}
                          users={users}
                          userByTeamAndRound={userByTeamAndRound}
                          matchupResults={matchupResults}
                          onOpenGame={gameId => navigate(`/games/${gameId}`)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </article>
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}

function PlayoffGameRow({
  scheduleGame,
  users,
  userByTeamAndRound,
  matchupResults,
  onOpenGame,
}: {
  scheduleGame: PlayoffScheduleSlot
  users: PlayoffCompetitorInput[]
  userByTeamAndRound: Map<string, { id: number; displayName: string; groupPlaySlot: string | null }>
  matchupResults: Map<string, PlayoffMatchupResult[]>
  onOpenGame: (gameId: number) => void
}) {
  const { homeUser, awayUser, seriesGameIndex } = scheduleGame
  const resultKey = homeUser && awayUser ? getUserMatchupKey(homeUser.id, awayUser.id) : null
  const matchupRecords = resultKey ? matchupResults.get(resultKey) ?? [] : []
  const matchupRecord =
    homeUser && awayUser
      ? resolveVisibleMatchupRecordForPlayoffRound(
          matchupRecords,
          homeUser,
          awayUser,
          scheduleGame.playoffRound,
          users,
          userByTeamAndRound,
          matchupResults,
          seriesGameIndex
        )
      : undefined
  const homeResult = homeUser ? matchupRecord?.scoresByUser[homeUser.id] : undefined
  const awayResult = awayUser ? matchupRecord?.scoresByUser[awayUser.id] : undefined
  const gameId = matchupRecord?.gameId
  const isClickable = typeof gameId === 'number'

  return (
    <tr
      className={isClickable ? 'group-play-matchup-row clickable' : 'group-play-matchup-row'}
      onClick={isClickable ? () => onOpenGame(gameId) : undefined}
      onKeyDown={
        isClickable
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onOpenGame(gameId)
              }
            }
          : undefined
      }
      role={isClickable ? 'link' : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      <td className="group-play-player-cell">
        <div className="group-play-player-stack">
          <div className="group-play-player-name">
            {homeUser?.displayName ??
              `${getLeagueTeamDisplayName(scheduleGame.homeTeamName)} ${scheduleGame.displayRoundLabel}`}
          </div>
          {homeResult ? (
            matchupRecord?.isForfeit ? (
              <ForfeitMarker result={homeResult.forfeitResult} />
            ) : (
              <div className="group-play-score-row group-play-score-row-home">
                <TeamHelmet
                  abbreviation={homeResult.teamAbbreviation}
                  size="sm"
                  className="group-play-score-helmet"
                />
                <span className="group-play-score-value">{homeResult.score}</span>
              </div>
            )
          ) : null}
        </div>
      </td>
      <td className="group-play-vs-cell">
        <span
          className={
            scheduleGame.displayRoundLabel === 'Rematch'
              ? 'playoff-round-label playoff-round-label--rematch'
              : 'playoff-round-label'
          }
        >
          {scheduleGame.displayRoundLabel}
        </span>
      </td>
      <td className="group-play-player-cell group-play-player-cell-away">
        <div className="group-play-player-stack group-play-player-stack-away">
          <div className="group-play-player-name">
            {awayUser?.displayName ??
              `${getLeagueTeamDisplayName(scheduleGame.awayTeamName)} ${scheduleGame.displayRoundLabel}`}
          </div>
          {awayResult ? (
            matchupRecord?.isForfeit ? (
              <ForfeitMarker result={awayResult.forfeitResult} align="away" />
            ) : (
              <div className="group-play-score-row group-play-score-row-away">
                <span className="group-play-score-value">{awayResult.score}</span>
                <TeamHelmet
                  abbreviation={awayResult.teamAbbreviation}
                  size="sm"
                  className="group-play-score-helmet"
                />
              </div>
            )
          ) : null}
        </div>
      </td>
    </tr>
  )
}

function ForfeitMarker({
  result,
  align = 'home',
}: {
  result?: 'win' | 'loss'
  align?: 'home' | 'away'
}) {
  const isWin = result === 'win'
  return (
    <div className={`group-play-forfeit-marker group-play-forfeit-marker-${align}`}>
      <span className={isWin ? 'group-play-forfeit-win' : 'group-play-forfeit-loss'}>
        {isWin ? '✓' : '×'}
      </span>
      <span className="group-play-forfeit-label">{isWin ? 'Forfeit Win' : 'Forfeit Loss'}</span>
    </div>
  )
}
